import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

const helpTopics = {
  matchPlayRules: {
    title: 'Reikäpelin säännöt',
    intro: 'Tiivistelmä perustuu Suomen frisbeegolfliiton kilpailuoppaan reikäpelisääntöihin.',
    summaryTitle: 'Säännöt lyhyesti',
    summary: 'Kisoissa noudatetaan reikäpelin virallisia sääntöjä. Poikkeuksena on vain, että pari saattaa tehdä oman kierroksensa ilman ulkopuolisia valvojia.',
    highlights: [
      'Reikäpelissä kaksi pelaajaa pelaa väylävoitoista. Ottelun voittaa pelaaja, joka voittaa enemmän väyliä.',
      'Ensimmäisellä väylällä avausjärjestys tulee tuloskortin mukaisesti. Seuraavilla väylillä edellisen väylän voittaja aloittaa. Tasatilanne ei muuta järjestystä.',
      'Väylän voittaa pelaaja, joka pelaa väylän loppuun pienemmällä heittomäärällä. Tasatuloksessa väylä menee tasan.',
      'Kun pelaaja ei voi enää voittaa väylää, pelaamista ei jatketa kyseisellä väylällä. Väylän jälkeen heitetyt lisäheitot ovat harjoitusheittoja.',
      'Pelaaja voi luovuttaa yksittäisen väylän, antaa vastustajalle heiton tai luovuttaa koko ottelun. Annettua heittoa tai luovutusta ei voi perua.',
      'Ottelu päättyy, kun toinen pelaaja on enemmän väyliä voitolla kuin väyliä on jäljellä. Jos viimeisen väylän jälkeen ollaan tasan, kilpailunjohtaja ratkaisee tasapelin menettelyn.',
    ],
    sourceLabel: 'Virallinen lähde',
    sourceText: 'Suomen frisbeegolfliiton sääntökirja ja kilpailuopas, Liite A: Reikäpelin säännöt.',
    sourceUrl: 'https://frisbeegolfliitto.fi/frisbeegolfin-virallinen-saantokirja-ja-kilpailuopas/#reikapelin',
    sourceCta: 'Avaa viralliset säännöt',
  },
  competitionGuidelines: {
    title: 'Kilpailun yleiset ohjeet',
    intro: 'Lyhyt yhteenveto kilpailun peruskäytännöistä pelaajille.',
    summaryTitle: 'Kierrosajankohdan ja radan sopiminen',
    summary: 'Kilpailun tiedoissa näytetään pelaajan seuraava vastustaja heti kun se on selvinnyt. Vastustajan yhteystiedot löytyvät nimeä klikkaamalla.',
    highlights: [
      'Pelattavan radan valitsee pelaaja, jonka pelattujen väylien voittoprosentti on pienempi.',
      'Jos voittoprosentit ovat samat, verrataan ensin tappiottomien väylien prosenttia ja sen jälkeen pelattujen väylien määrää. Jos ratkaisua ei silti saada, radan valitseva pelaaja arvotaan.',
      'Kisassa mukana olevat radat löytyvät kisainfosta, mutta pelaajat voivat myös sopia pelaavansa muulla radalla, jos se sopii molemmille eikä sitä ole erikseen kielletty kisainfossa.',
      'Pelaajien tulee varmistaa, ettei heidän sopimallaan radalla ole muuta tapahtumaa, ja heidän tulee muutenkin kunnioittaa radan sääntöjä ja mahdollisia poikkeuksia.',
      'Jos esimerkiksi joku väylä on pois käytöstä, se jätetään välistä ja kierroksen jälkeen jatketaan taas väylältä yksi, kunnes 18 väylää on pelattu, mikäli peli ei ole siihen mennessä ratkennut.',
      'Vaikka pelaajilla on keskinäinen kisa menossa, heillä ei ole etuoikeutta radalle, vaan kierros pelataan kuten tavallisella harjoituskierroksella.',
      'Pelaa reilusti ja ilmoita sääntörikkeistä heti, jos havaitset sellaisen.',
      'Seuraa oman ottelusi tilannetta ja varmista ennen kierrosta, kuka on vastustajasi ja milloin ottelusi alkaa.',
      'Pidä pelin tempo sujuvana. Siirry väylältä toiselle viivyttelemättä ja ole valmiina omalla vuorollasi.',
      'Jos sääntötulkinta on epäselvä, sopikaa etenemistapa heti ja ratkaiskaa asia kilpailunjohtajan ohjeiden mukaan.',
      'Mahdolliset paikallissäännöt, kuten OB:t, mandot tai erikoisvapautukset, ovat aina kilpailukohtaisesti voimassa kilpailun tiedotuksen mukaan.',
    ],
  },
  paymentAndCancellation: {
    title: 'Maksu- ja peruutusehdot',
    intro: 'Ohjeet kilpailumaksun hoitamiseen ja rekisteröinnin peruuttamiseen.',
    summaryTitle: 'Maksut ja peruutukset',
    summary: 'Osallistumismaksuja ei palauteta. Maksa osallistuminen vasta kun tiedät, että pystyt osallistumaan kisaan.',
    highlights: [      
      'Kisa saattaa kestää useita viikkoja, joten jos tiedät jo etukäteen, että tulet olemaan poissa useamman viikon kisan aikana, älä osallistu.',
      'Mikäli kisan osallistujamäärä on rajoitettu, kisa alkaa heti kun maksimimäärä kilpailijoita on maksanut osallistumismaksun.',
      'Maksutiedot löytyvät kilpailun tiedoista. Käytä maksaessa järjestäjän ohjeistamaa viitettä tai muuta tunnistetietoa.',
      'Epäselvissä maksuasioissa ota yhteys kilpailun järjestäjään mahdollisimman aikaisin.',
    ],
  },
  scoringInstructions: {
    title: 'Kirjaamisohjeet',
    intro: 'Näin kirjaat ottelun väylätulokset sovelluksessa.',
    summaryTitle: 'Tulosten kirjaaminen',
    summary: 'Kirjauksessa merkitään jokaisen väylän voittaja. Väylän voittanut pelaaja saa pisteen ja tasatuloksessa väylä jää ilman pistettä.',
    highlights: [
      'Avaa kirjaus omasta ottelustasi ja varmista, että oikea väylä on valittuna ennen merkintää.',
      'Valitse väylän voittanut pelaaja. Jos väylä menee tasan, merkitse väylä tasapeliksi.',
      'Nuolipainikkeilla voit siirtyä edelliselle tai seuraavalle väylälle ja tarkistaa aiemmat merkinnät.',
      'Aloittajan merkki näyttää, kumpi pelaaja aloittaa kyseisen väylän. Ensimmäisellä väylällä aloittaja määräytyy järjestelmän mukaan, ja sen jälkeen aloittajaksi vaihtuu viimeksi pisteen saanut pelaaja.',
      'Kun ottelu on valmis, tarkista merkinnät vielä yhdessä vastustajan kanssa ja vahvista tulos sovelluksessa.',
    ],
  },
};

const MatchPlayRulesModal = ({ isOpen, onClose, topic = 'matchPlayRules' }) => {
  const content = helpTopics[topic] || helpTopics.matchPlayRules;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-[28px] border border-white/10 bg-white p-6 shadow-[0_30px_90px_-40px_rgba(15,23,42,0.55)] sm:p-7"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">Ohjeet ja säännöt</p>
            <h2 className="mt-2 text-2xl font-black text-slate-900">{content.title}</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {content.intro}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            Sulje
          </button>
        </div>

        <div className="mt-6 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-4 text-sm leading-6 text-slate-700">
          <p className="font-semibold text-slate-900">{content.summaryTitle}</p>
          <p className="mt-2">
            {content.summary}
          </p>
        </div>

        <div className="mt-6 space-y-3">
          {content.highlights.map((rule, index) => (
            <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700">
              {rule}
            </div>
          ))}
        </div>

        {content.sourceUrl ? (
          <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-700 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-semibold text-slate-900">{content.sourceLabel}</p>
              <p className="mt-1 text-slate-600">{content.sourceText}</p>
            </div>
            <a
              href={content.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex justify-center rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800"
            >
              {content.sourceCta}
            </a>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default MatchPlayRulesModal;